import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FileMeta,
  FilePreview,
  SpreadsheetSheet,
  SlideBlock,
  SlideShape,
  getPreview,
  FileWorkspaceError,
  officeEngine,
} from './fileWorkspaceService';

const OfficeFilePreview = lazy(() =>
  import('./OfficeFilePreview').then((m) => ({ default: m.OfficeFilePreview }))
);

function colLetter(index: number): string {
  let n = index + 1;
  let out = '';
  while (n > 0) {
    n -= 1;
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26);
  }
  return out;
}

const SpreadsheetView: React.FC<{
  preview: Extract<FilePreview, { kind: 'spreadsheet' }>;
  theme: 'light' | 'dark';
  c: any;
}> = ({ preview, theme, c }) => {
  const [sheet, setSheet] = useState(0);
  useEffect(() => { setSheet(0); }, [preview.filename]);
  const current: SpreadsheetSheet | undefined = preview.sheets[sheet] || preview.sheets[0];
  const rows = current?.rows || [];
  const colCount = Math.max(
    ...rows.map((row) => row.length),
    1,
  );
  const grid = theme === 'dark' ? '#2A2C30' : '#D0D0D0';
  const headerBg = theme === 'dark' ? '#1C1E21' : '#F2F2F2';
  const headerFg = theme === 'dark' ? '#A8ADB4' : '#5F6368';
  const cellBg = theme === 'dark' ? '#141619' : '#FFFFFF';
  const stage = theme === 'dark' ? '#0F1012' : '#F3F3F3';
  const tabBar = theme === 'dark' ? '#16181B' : '#E6E6E6';
  const excelGreen = '#217346';

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, background: stage }}>
      <div
        style={{
          flexShrink: 0,
          height: '28px',
          display: 'flex',
          alignItems: 'center',
          padding: '0 10px',
          fontSize: '11px',
          color: headerFg,
          borderBottom: `1px solid ${grid}`,
          background: headerBg,
          fontFamily: c.mono,
          gap: '10px',
        }}
      >
        <span style={{ fontWeight: 600, color: excelGreen }}>A1</span>
        <span style={{ opacity: 0.7 }}>
          {current ? `${current.total_rows} baris · ${current.total_cols} kolom` : ''}
          {current?.truncated ? ' · cuplikan' : ''}
        </span>
      </div>
      <div className="custom-scrollbar" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <table
          style={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            fontSize: '12px',
            fontFamily: 'Calibri, "Segoe UI", Arial, sans-serif',
            minWidth: '100%',
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  position: 'sticky',
                  top: 0,
                  left: 0,
                  zIndex: 3,
                  width: '36px',
                  minWidth: '36px',
                  background: headerBg,
                  borderRight: `1px solid ${grid}`,
                  borderBottom: `1px solid ${grid}`,
                }}
              />
              {Array.from({ length: colCount }, (_, i) => (
                <th
                  key={i}
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    minWidth: '84px',
                    padding: '4px 8px',
                    background: headerBg,
                    color: headerFg,
                    fontWeight: 600,
                    fontSize: '11px',
                    textAlign: 'center',
                    borderRight: `1px solid ${grid}`,
                    borderBottom: `1px solid ${grid}`,
                  }}
                >
                  {colLetter(i)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={r}>
                <th
                  style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    width: '36px',
                    minWidth: '36px',
                    padding: '3px 6px',
                    background: headerBg,
                    color: headerFg,
                    fontWeight: 600,
                    fontSize: '11px',
                    textAlign: 'right',
                    borderRight: `1px solid ${grid}`,
                    borderBottom: `1px solid ${grid}`,
                  }}
                >
                  {r + 1}
                </th>
                {Array.from({ length: colCount }, (_, i) => {
                  const fill = current?.fills?.[r]?.[i];
                  const bold = current?.bolds?.[r]?.[i];
                  const darkFill = fill && fill.replace('#', '').match(/^[0-4]/);
                  return (
                    <td
                      key={i}
                      style={{
                        padding: '3px 8px',
                        borderRight: `1px solid ${grid}`,
                        borderBottom: `1px solid ${grid}`,
                        background: fill || cellBg,
                        color: fill ? (darkFill ? '#fff' : '#111') : (theme === 'dark' ? c.ink2 : '#222'),
                        fontWeight: bold ? 700 : 400,
                        whiteSpace: 'nowrap',
                        maxWidth: '280px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {row[i] || ''}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'stretch',
          height: '28px',
          background: tabBar,
          borderTop: `1px solid ${grid}`,
          overflowX: 'auto',
        }}
      >
        {preview.sheets.map((item, i) => {
          const active = i === sheet;
          return (
            <button
              key={`${item.name}-${i}`}
              type="button"
              onClick={() => setSheet(i)}
              style={{
                height: '100%',
                padding: '0 14px',
                border: 'none',
                borderRight: `1px solid ${grid}`,
                borderTop: active ? `2px solid ${excelGreen}` : '2px solid transparent',
                background: active ? cellBg : 'transparent',
                color: active ? excelGreen : headerFg,
                fontSize: '12px',
                cursor: 'pointer',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
              }}
            >
              {item.name}
            </button>
          );
        })}
        {(current?.truncated || preview.truncated) && (
          <span style={{ marginLeft: '10px', alignSelf: 'center', fontSize: '11px', color: headerFg }}>
            Cuplikan. Unduh file untuk versi lengkap.
          </span>
        )}
      </div>
    </div>
  );
};

const DocumentView: React.FC<{ html: string; c: any }> = ({ html, c }) => (
  <div
    className="custom-scrollbar"
    style={{
      height: '100%',
      overflow: 'auto',
      padding: '28px 32px 40px',
      color: c.ink,
      fontSize: '14.5px',
      lineHeight: 1.65,
      maxWidth: '760px',
    }}
  >
    <style>{`
      .pa-doc h1, .pa-doc h2, .pa-doc h3, .pa-doc h4 { margin: 1.2em 0 0.4em; font-weight: 650; line-height: 1.3; }
      .pa-doc h1 { font-size: 22px; } .pa-doc h2 { font-size: 18px; } .pa-doc h3 { font-size: 16px; }
      .pa-doc p { margin: 0 0 0.85em; }
      .pa-doc li { margin: 0 0 0.35em; }
      .pa-doc table { border-collapse: collapse; margin: 12px 0 18px; width: 100%; font-size: 13px; }
      .pa-doc th, .pa-doc td { border: 1px solid ${c.line}; padding: 6px 8px; text-align: left; }
      .pa-doc th { background: ${c.panel2}; }
    `}</style>
    <div className="pa-doc" dangerouslySetInnerHTML={{ __html: html }} />
  </div>
);

const SlidesView: React.FC<{
  preview: Extract<FilePreview, { kind: 'slides' }>;
  c: any;
}> = ({ preview, c }) => {
  const [current, setCurrent] = useState(0);
  useEffect(() => { setCurrent(0); }, [preview.filename]);
  const slide = preview.slides[current] || preview.slides[0];
  const shapes: SlideShape[] = (slide?.shapes || []) as SlideShape[];
  const ratio = slide?.ratio || 16 / 9;
  const notes = slide?.notes || '';
  const pptOrange = '#C43E1C';

  const renderShape = (shape: SlideShape, i: number) => {
    const box: React.CSSProperties = {
      position: 'absolute',
      left: `${shape.x * 100}%`,
      top: `${shape.y * 100}%`,
      width: `${Math.max(shape.w, 0.02) * 100}%`,
      height: `${Math.max(shape.h, 0.02) * 100}%`,
      overflow: 'hidden',
      boxSizing: 'border-box',
    };
    if (shape.type === 'image') {
      return shape.src ? (
        <img
          key={i}
          src={shape.src}
          alt=""
          style={{ ...box, objectFit: 'contain' }}
        />
      ) : (
        <div
          key={i}
          style={{
            ...box,
            background: '#E8E8E8',
            border: '1px solid #D0D0D0',
          }}
        />
      );
    }
    if (shape.type === 'table') {
      return (
        <div key={i} style={box}>
          <table
            style={{
              borderCollapse: 'collapse',
              width: '100%',
              height: '100%',
              fontSize: '11px',
              background: '#fff',
            }}
          >
            <tbody>
              {shape.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      style={{
                        border: '1px solid #C8C8C8',
                        padding: '4px 6px',
                        color: '#222',
                      }}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    const pt = shape.pt || (shape.h > 0.12 ? 28 : 16);
    return (
      <div
        key={i}
        style={{
          ...box,
          color: '#222',
          fontSize: `clamp(11px, ${pt * 0.09}cqw, 36px)`,
          fontWeight: shape.h > 0.12 ? 650 : 400,
          lineHeight: 1.25,
          fontFamily: '"Calibri", "Segoe UI", Arial, sans-serif',
          padding: '2px 4px',
          whiteSpace: 'pre-wrap',
        }}
      >
        {shape.text}
      </div>
    );
  };

  return (
    <div style={{ height: '100%', display: 'flex', minHeight: 0, background: '#2B2B2B' }}>
      <div
        className="custom-scrollbar"
        style={{
          width: '128px',
          flexShrink: 0,
          overflowY: 'auto',
          padding: '12px 10px',
          background: '#1E1E1E',
          borderRight: '1px solid #3A3A3A',
        }}
      >
        {preview.slides.map((item, i) => (
          <button
            key={item.index}
            type="button"
            onClick={() => setCurrent(i)}
            style={{
              display: 'block',
              width: '100%',
              marginBottom: '10px',
              padding: 0,
              background: 'none',
              border: `2px solid ${i === current ? pptOrange : 'transparent'}`,
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            <div
              style={{
                position: 'relative',
                width: '100%',
                paddingBottom: `${100 / (item.ratio || ratio)}%`,
                background: '#fff',
                overflow: 'hidden',
              }}
            >
              <div style={{ position: 'absolute', inset: 4, fontSize: 8, color: '#666', textAlign: 'left' }}>
                {(item.shapes || []).filter((s) => s.type === 'text').slice(0, 2).map((s) =>
                  s.type === 'text' ? s.text : ''
                ).join(' · ').slice(0, 48) || `Slide ${item.index}`}
              </div>
            </div>
            <div style={{ fontSize: '10px', color: i === current ? '#fff' : '#A0A0A0', padding: '4px 0 2px' }}>
              {item.index}
            </div>
          </button>
        ))}
      </div>
      <div
        className="custom-scrollbar"
        style={{
          flex: 1,
          minWidth: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          padding: '20px 24px 16px',
        }}
      >
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
          <div style={{ width: 'min(100%, 960px)' }}>
            <div
              style={{
                position: 'relative',
                width: '100%',
                paddingBottom: `${100 / ratio}%`,
                background: '#fff',
                boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
                containerType: 'size',
              }}
            >
              <div style={{ position: 'absolute', inset: 0 }}>
                {shapes.length > 0
                  ? shapes.map(renderShape)
                  : (slide?.blocks || []).map((block: SlideBlock, i) => (
                      <div
                        key={i}
                        style={{ padding: '18px 22px', color: '#222', fontSize: 16 }}
                      >
                        {block.type === 'table' ? 'Tabel' : block.text}
                      </div>
                    ))}
              </div>
            </div>
          </div>
        </div>
        {notes ? (
          <div style={{ marginTop: '12px', fontSize: '12px', color: '#C8C8C8', lineHeight: 1.45 }}>
            Catatan: {notes}
          </div>
        ) : null}
        {preview.truncated && (
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#8A8A8A' }}>
            Cuplikan saja. Unduh file untuk semua slide.
          </div>
        )}
      </div>
    </div>
  );
};

interface FilePreviewPaneProps {
  file: FileMeta;
  themeColors: any;
  theme: 'light' | 'dark';
  onDownload: (file: FileMeta) => void;
}

export const FilePreviewPane: React.FC<FilePreviewPaneProps> = ({
  file,
  themeColors,
  theme,
  onDownload,
}) => {
  const c = themeColors[theme];
  const [preview, setPreview] = useState<FilePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nativeFailed, setNativeFailed] = useState(false);
  const engine = officeEngine(file.mime, file.filename);
  const useNative = Boolean(engine) && !nativeFailed;
  const onNativeFail = useCallback(() => setNativeFailed(true), []);

  useEffect(() => {
    setNativeFailed(false);
  }, [file.id]);

  useEffect(() => {
    if (useNative) {
      setPreview(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setPreview(null);
    setError(null);
    getPreview(file.id)
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof FileWorkspaceError && e.status === 404) {
          setError('File sudah dihapus.');
        } else {
          setError(e instanceof FileWorkspaceError ? e.detail : 'File tidak bisa dibuka.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [file.id, useNative]);

  return (
    <div style={{
      flex: 1,
      minHeight: 0,
      overflow: useNative ? 'hidden' : 'auto',
      height: '100%',
      background: theme === 'dark' ? '#0A0B0D' : '#F4F5F6',
    }}>
      {useNative && engine && (
        <Suspense fallback={<div style={{ padding: '28px 24px', fontSize: '13px', color: c.ink3 }}>Membuka…</div>}>
          <OfficeFilePreview file={file} engine={engine} onFail={onNativeFail} />
        </Suspense>
      )}
      {!useNative && error && (
        <div style={{ padding: '28px 24px', fontSize: '13px', color: c.ink2 }}>{error}</div>
      )}
      {!useNative && !error && !preview && (
        <div style={{ padding: '28px 24px', fontSize: '13px', color: c.ink3 }}>Membuka…</div>
      )}
      {preview?.kind === 'image' && (
        <div style={{ height: '100%', display: 'grid', placeItems: 'center', padding: '16px' }}>
          <img
            src={preview.url}
            alt={file.filename}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        </div>
      )}
      {preview?.kind === 'pdf' && (
        <iframe
          title={file.filename}
          src={preview.url}
          style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
        />
      )}
      {preview?.kind === 'text' && (
        <pre
          style={{
            margin: 0,
            padding: '20px 22px',
            fontSize: '12.5px',
            lineHeight: 1.55,
            color: c.ink,
            fontFamily: c.mono,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {preview.text}
          {preview.truncated ? '\n\n... teks dipotong. Unduh untuk versi lengkap.' : ''}
        </pre>
      )}
      {preview?.kind === 'spreadsheet' && (
        <SpreadsheetView preview={preview} theme={theme} c={c} />
      )}
      {preview?.kind === 'html' && (
        <iframe
          title={file.filename}
          sandbox=""
          srcDoc={preview.html}
          style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
        />
      )}
      {preview?.kind === 'document' && (
        <DocumentView html={preview.html} c={c} />
      )}
      {preview?.kind === 'slides' && (
        <SlidesView preview={preview} c={c} />
      )}
      {preview?.kind === 'other' && (
        <div style={{ padding: '28px 24px', fontSize: '13px', color: c.ink2, lineHeight: 1.55 }}>
          {preview.summary || 'File ini tidak bisa ditampilkan di sini.'}
          <div style={{ marginTop: '14px' }}>
            <button
              type="button"
              onClick={() => onDownload(file)}
              style={{
                height: '32px',
                padding: '0 12px',
                borderRadius: '8px',
                border: `1px solid ${c.line}`,
                background: c.panel,
                color: c.ink,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: '12.5px',
              }}
            >
              Unduh file
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

interface FilePreviewModalProps {
  file: FileMeta;
  themeColors: any;
  theme: 'light' | 'dark';
  onClose: () => void;
  onDownload: (file: FileMeta) => void;
}

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  file,
  themeColors,
  theme,
  onClose,
  onDownload,
}) => {
  const c = themeColors[theme];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={file.filename}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        background: 'rgba(0,0,0,0.62)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(880px, 96vw)',
          height: 'min(720px, 90vh)',
          background: theme === 'dark' ? '#101215' : '#FFFFFF',
          border: `1px solid ${c.line}`,
          borderRadius: '14px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            height: '50px',
            padding: '0 16px',
            borderBottom: `1px solid ${c.line}`,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: '13.5px',
              fontWeight: 600,
              color: c.ink,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={file.filename}
          >
            {file.filename}
          </div>
          <button
            type="button"
            onClick={() => onDownload(file)}
            style={{
              background: 'none',
              border: `1px solid ${c.line}`,
              borderRadius: '8px',
              height: '30px',
              padding: '0 10px',
              color: c.ink2,
              fontSize: '12px',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Unduh
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Tutup"
            style={{
              background: 'none',
              border: 'none',
              color: c.ink3,
              fontSize: '20px',
              lineHeight: 1,
              cursor: 'pointer',
              padding: '0 4px',
            }}
          >
            ×
          </button>
        </div>
        <FilePreviewPane file={file} theme={theme} themeColors={themeColors} onDownload={onDownload} />
      </div>
    </div>,
    document.body
  );
};
